import { ExclamationCircleFilled, MinusCircleOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { awardsAPI } from "@api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Col, Form, Input, InputNumber, Radio, Row, Space, Typography, Upload, notification, message, Modal, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const { Title } = Typography;
const { Item } = Form;
const { TextArea } = Input;
const { confirm } = Modal;


const CreateEditAwardMobile = () => {

  const { t } = useTranslation("common");
  const { t: tError } = useTranslation("error");
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [file, setFile] = useState([]);

  const { data: award, isLoading: loadingAward } = useQuery({
    queryKey: ['award', id],
    queryFn: () => awardsAPI.getOne(id).then(({ data }) => data),
    onError: ({ response }) => {
      notification.error({
        message: tError(response.data.message),
      })
    },
    enabled: !!id,
  })

  const { mutate: deleteAwardMutation, isLoading: loadingDelete } = useMutation({
    mutationFn: awardsAPI.deleteAward,
    onSuccess: () => {
      navigate('/awards')
      message.success(t("awardTable.delete.onSucces"))
    },
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const { mutate, isLoading: confirmLoading } = useMutation({
    mutationFn: ({ id, values }) => {
      if (award) {
        return awardsAPI.updateAward(id, values);
      } else {
        return awardsAPI.createAward(values);
      }
    },
    onSuccess: () => {
      navigate('/awards')
      form.resetFields();
      message.success(t(`awardTable.form.${award ? 'notificationEdit' : 'notifiacionCreate'}`))
    },
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const showDeactivateAwardConfirm = (award) => {
    confirm({
      title: t("awardTable.delete.confirm"),
      icon: <ExclamationCircleFilled />,
      content: t("awardTable.delete.description"),
      okType: "danger",
      okText: t("awardTable.delete.ok"),
      cancelText: t("awardTable.delete.cancel"),
      maskClosable: true,
      centered: true,
      onOk: () => deleteAwardMutation(award.id),
    });
  };

  useEffect(() => {
    if (award) {
      form.setFieldsValue({ ...award });
      setFile([
        {
          uid: award?.imageUrl,
          thumbUrl: award?.imageUrl,
          url: award?.imageUrl,
        },
      ]);
    }
  }, [award]);

  return (
    <Row style={{ background: '#fff' }}>
      <Row style={{ margin: '16px' }}>
        <Col span={24}>
          <Title level={4} style={{ margin: 0 }} > {award ? t("awardTable.edit") : t("awardTable.add")} </Title>
        </Col>
        <Spin spinning={loadingAward && !!id}>
          <Form
            form={form}
            name="awardForm"
            onFinish={(values) => mutate({ id: award?.id, values })}
            autoComplete="off"
            scrollToFirstError
            layout="horizontal"
            requiredMark={false}
            style={{ width: '100%' }}
          >
            <Row justify="start" gutter={[16, 16]}>
              <Col span={24}>
                <Item
                  style={{ margin: 0 }}
                  label={t("awardTable.name")}
                  name="name"
                  rules={[{ required: true, message: t("awardTable.form.name") }]}
                >
                  <Input placeholder={t("awardTable.form.placeholderName")} />
                </Item>
              </Col>
              <Col span={24}>
                <Item
                  style={{ margin: 0 }}
                  label={t("awardTable.description")}
                  name="description"
                  rules={[
                    { required: true, message: t("awardTable.form.description") },
                  ]}
                >
                  <TextArea rows={4} style={{ resize: "none" }} placeholder={t("awardTable.form.placeholderDescription")} />
                </Item>
              </Col>
              <Col span={24}>
                <Form.List
                  name="amount"
                  rules={[{ required: true, message: 'At least one amount is required' }]}
                >
                  {(fields, { add, remove }, { errors }) => (
                    <>
                      <Form.Item>
                        <Space>
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            icon={<PlusOutlined />}
                          >
                            Add Amount
                          </Button>
                          <Form.ErrorList errors={errors} />
                        </Space>
                      </Form.Item>

                      {fields.map(({ key, name, ...restField }) => (
                        <Row key={key}>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={name}
                              rules={[{ required: true, message: 'Enter amount' }]}
                            >
                              <InputNumber step={5} min={0} style={{ width: "100%" }} placeholder={t("awardTable.form.placeHolderAmount")} />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Button
                              icon={<MinusCircleOutlined />}
                              type="text"
                              danger
                              onClick={() => remove(name)}
                            />
                          </Col>
                        </Row>
                      ))}
                    </>
                  )}
                </Form.List>
              </Col>
              <Col span={24}>
                <Item
                  name="available"
                  label={t("awardTable.form.availability")}
                  style={{ margin: 0 }}
                  rules={[{ required: true, message: t("awardTable.form.available") }]}
                >
                  <Radio.Group>
                    <Radio value={true}> {t("awardTable.form.ratioAvailable")} </Radio>
                    <Radio value={false}> {t("awardTable.form.ratioUnavailable")} </Radio>
                  </Radio.Group>
                </Item>
              </Col>
              <Col span={24}>
                <Item
                  label={t("awardTable.imageUrl")}
                  name="attachment"
                  rules={[
                    {
                      required: !award,
                      message: t("awardTable.form.imageUrl"),
                    },
                  ]}
                  getValueFromEvent={({ file }) => file}
                >
                  <Upload
                    listType="picture"
                    maxCount={1}
                    accept="image/png, image/jpeg, image/jpg"
                    beforeUpload={() => false}
                    multiple={false}
                    file={file}
                    onChange={({ file }) => setFile(file)}
                  >
                    <Button icon={<UploadOutlined />}>
                      {t("userTable.form.btnUpload")}
                    </Button>
                  </Upload>
                </Item>
              </Col>
            </Row>

            <Col span={24}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button style={{ width: '100%' }} type="primary" htmlType="submit" loading={confirmLoading | loadingDelete} > {award ? t("awardTable.form.btnSaveChanges") : t("awardTable.form.btnCreate")} </Button>
                {award ?
                  <>
                    <Button style={{ width: '100%' }} onClick={() => navigate('/awards')} loading={confirmLoading | loadingDelete}> {t("awardTable.form.btnDiscardChanges")} </Button>
                    <Button style={{ width: '100%' }} danger onClick={() => showDeactivateAwardConfirm(award)} loading={confirmLoading | loadingDelete} > {t("awardTable.delete.default")} </Button>
                  </>
                  :
                  <Button style={{ width: '100%' }} onClick={() => navigate('/awards')} loading={confirmLoading | loadingDelete} > {t("awardTable.form.btnCancel")} </Button>
                }
              </Space>
            </Col>
          </Form>
        </Spin>
      </Row >
    </Row >
  )
}

export default CreateEditAwardMobile;